<template>
  <div v-if="data">
    <!-- <p
      v-if="label"
      class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 font-bold text-lg text-center max-w-[150px] max-h-[150px] text-ellipsis"
    >{{label}}</p>-->
    <Doughnut
      :chart-options="chartOptions"
      :chart-data="chartData"
      :chart-id="chartId"
      :dataset-id-key="datasetIdKey"
      :plugins="[...plugins, labelPlugin, textPlugin]"
      :css-classes="cssClasses"
      :styles="styles"
      :width="width"
      :height="height"
    />
  </div>
</template>

<script>
/* eslint-disable */
import { Doughnut } from 'vue-chartjs';

import { Chart as ChartJS, Title, Tooltip, Legend, ArcElement, CategoryScale } from 'chart.js';
import { CHART_COLORS, COLORS } from './COLORS';

ChartJS.register(Title, Tooltip, Legend, ArcElement, CategoryScale);

export default {
  components: {
    Doughnut,
  },
  props: {
    labelText: String,
    data: Array,
    chartId: {
      type: String,
      default: 'doughnut-chart',
    },
    datasetIdKey: {
      type: String,
      default: 'label',
    },
    width: {
      type: Number,
      default: 400,
    },
    height: {
      type: Number,
      default: 400,
    },
    cssClasses: {
      default: '',
      type: String,
    },
    styles: {
      type: Object,
      default: () => {},
    },
    plugins: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      defaultColors: [],
      chartDefaultData: {
        labels: [],
        datasets: [
          {
            backgroundColor: [
              '#41B883',
              '#F9DC5C',
              '#963A41',
              '#2F3061',
              '#FF7F51',
              '#3E505B',
              '#DBEFBC',
              '#241E4E',
              '#F5EE9E',
            ],
            data: [],
          },
        ],
      },
      chartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '70%',
        layout: {
          padding: 40,
        },

        plugins: {
          legend: false,
          tooltip: {
            callbacks: {
              label: (context) => {
                const defaultVal = this.data[context.dataIndex].default_value;
                let label = ` ${context.label}` || '';
                if (label) {
                  label += ': ';
                }

                if (defaultVal !== undefined) {
                  label += `${defaultVal}`;
                }
                if (context.formattedValue) {
                  label += ` (${Math.round(context.formattedValue * 10) / 10}%)`;
                }
                return label;
              },
            },
          },
        },
      },
    };
  },

  computed: {
    labelPlugin() {
      const text = this.labelText;
      const dataLng = this.data.length || 4;
      const cWidth = document.body.clientWidth;

      let txtoffsety = 5 * dataLng;
      let fontSize = 1.5;

      if (cWidth < 400) {
        txtoffsety = 10 * dataLng;
        fontSize = 0.85;
      } else if (cWidth < 900) {
        txtoffsety = 6 * dataLng;
        fontSize = 1.2;
      }
      return {
        id: 'custom_canvas_label',
        beforeDraw(chart) {
          const { width } = chart;
          const { height } = chart;
          const { ctx } = chart;

          ctx.restore();
          // const fontSize = (height / 114).toFixed(2);
          ctx.font = `${fontSize}em sans-serif`;
          ctx.textBaseline = 'middle';

          const textX = Math.round((width - ctx.measureText(text).width) / 2);
          const textY = height / 2 + txtoffsety;

          ctx.fillText(text, textX, textY - textY * 0.13);
          ctx.save();
        },
      };
    },

    chartData() {
      const data = this.chartDefaultData;
      const labels = [];
      const dataSet = [];
      this.data.forEach((el) => {
        let label = el.label.replace('_', ' ');
        if (label.includes('count')) {
          label = label.replace('count', '');
        }
        labels.push(label);
        dataSet.push(el.value);
      });

      let colors = [];
      if (this.data && Array.isArray(this.data)) {
        // CHART_COLORS
        this.data.forEach((el, ix) => {
          if (el?.label) {
            let key = el?.label;

            if (key.includes('_count')) {
              key = key.replace('_count', '');
            }

            colors.push(CHART_COLORS[key] || COLORS[ix]);
          } else {
            colors.push(COLORS[ix]);
          }
        });
      }
      if (!colors.length) {
        colors = [...COLORS];
      }

      data.labels = labels;
      data.datasets[0].data = dataSet;
      data.datasets[0].backgroundColor = colors;

      return data;
    },

    textPlugin() {
      //
      return {
        id: 'doughnut-outer-text',
        afterDraw(chart) {
          const {
            ctx,
            chartArea: { top, bottom, left, right, width, height },
          } = chart;

          // Store label positions to prevent overlaps
          const labelPositions = [];

          const capitalizeFirstLetter = (str) => {
            if (typeof str !== 'string') {
              throw new Error('Input is not a string');
            }
            if (str.length === 0) {
              return str;
            }
            return str.charAt(0).toUpperCase() + str.slice(1);
          };

          // Check if a position overlaps with existing labels
          const checkOverlap = (newPos, existingPositions, minDistance = 30) => {
            return existingPositions.some((pos) => {
              const distance = Math.sqrt(
                Math.pow(newPos.x - pos.x, 2) + Math.pow(newPos.y - pos.y, 2),
              );
              return distance < minDistance;
            });
          };

          // Find the best position for a label to avoid overlaps
          const findBestPosition = (baseX, baseY, halfW, halfH, existingPositions) => {
            const lineH = 35; // Increased for better spacing
            const lineEx = 30; // Increased for better spacing

            // Try different angles around the base position
            const angles = [0, 15, -15, 30, -30, 45, -45, 60, -60];

            for (let angle of angles) {
              const radians = (angle * Math.PI) / 180;
              const adjustedLineH = lineH + Math.abs(angle) * 0.5; // Vary line length

              const xLine =
                baseX >= halfW
                  ? baseX + adjustedLineH * Math.cos(radians)
                  : baseX - adjustedLineH * Math.cos(radians);
              const yLine =
                baseY >= halfH
                  ? baseY + adjustedLineH * Math.sin(radians)
                  : baseY - adjustedLineH * Math.sin(radians);

              const extraLine = baseX >= halfW ? lineEx : -lineEx;
              const finalX = xLine + extraLine;
              const finalY = yLine;

              if (!checkOverlap({ x: finalX, y: finalY }, existingPositions)) {
                return { xLine, yLine, extraLine, finalX, finalY };
              }
            }

            // If no position found, use default with increased spacing
            const xLine = baseX >= halfW ? baseX + lineH : baseX - lineH;
            const yLine =
              baseY >= halfH
                ? baseY + lineH + existingPositions.length * 20
                : baseY - lineH - existingPositions.length * 20;
            const extraLine = baseX >= halfW ? lineEx : -lineEx;

            return {
              xLine,
              yLine,
              extraLine,
              finalX: xLine + extraLine,
              finalY: yLine,
            };
          };

          chart.data.datasets.forEach((dataset, ix) => {
            chart.getDatasetMeta(ix).data.forEach((datapoints, inx) => {
              const { x, y } = datapoints.tooltipPosition();
              const val = dataset.data[inx] ? Math.round(dataset.data[inx] * 10) / 10 : 0;

              if (val && val >= 1) {
                // Draw the dot
                ctx.fillStyle = 'gray';
                ctx.fillRect(x - 1, y - 1, 3, 3); // Slightly larger dot

                const halfW = width / 2;
                const halfH = height / 2;

                // Find the best position for this label
                const position = findBestPosition(x, y, halfW, halfH, labelPositions);
                const { xLine, yLine, extraLine, finalX, finalY } = position;

                // Draw the line
                ctx.beginPath();
                ctx.moveTo(x, y);
                ctx.lineTo(xLine, yLine);
                ctx.lineTo(finalX, finalY);
                ctx.strokeStyle = 'gray';
                ctx.lineWidth = 1;
                ctx.stroke();

                // Prepare the text
                const secondTxt = capitalizeFirstLetter(chart.data.labels[inx]);
                const txt = `${val}% ${secondTxt}`;

                // Set text properties
                ctx.font = '0.85rem sans-serif';
                ctx.fillStyle = 'black';
                ctx.textBaseline = 'middle';
                ctx.textAlign = x >= halfW ? 'left' : 'right';
                const extraPad = x >= halfW ? 8 : -8; // Increased padding

                // Draw the text
                ctx.fillText(txt, finalX + extraPad, finalY);

                // Store this label position
                labelPositions.push({ x: finalX, y: finalY });
              }
            });
          });

          ctx.restore();
          ctx.save();
        },
      };
    },
  },
};
</script>
